import { debounce } from 'tiny-throttle';
import { getPreviewHash } from '../contexts/PreviewContext';
import { logError } from '../utils/logging';
import { getCookie } from '../utils/cookies';

export const API_ERRORS = {
  PREVIEW_FORBIDDEN: 'preview_forbidden',
};

export function stringifyUrlParams(obj) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
    } else {
      params.append(key, String(value));
    }
  }
  return params.toString();
}

export function getApiRequestConfig(method = 'GET') {
  const conf = {};
  const previewHash = getPreviewHash();
  const headers = previewHash ? { 'x-giftbox-preview': previewHash } : {};
  if (method.toLowerCase() === 'post') {
    headers['Content-Type'] = 'application/json; charset=utf-8';
    conf['method'] = 'POST';
    conf['credentials'] = 'same-origin';
  }
  conf['headers'] = headers;
  return conf;
}

export async function parseJson(fetchPromise) {
  const response = await fetchPromise;
  if (response.status >= 500) {
    throw Error(`Something unexpected happened. Status: ${response.status}`);
  }
  if (response.status >= 400) {
    throw Error(`Status: ${response.status}`);
  }
  return await response.json();
}

export function getCurrentLocale() {
  const path = window.location.pathname;
  const matches = /^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/gi.exec(path);
  return matches && matches[1] ? `${matches[1]}` : '';
}

export async function getProduct(handle) {
  let shopLocale = getCurrentLocale();
  shopLocale = shopLocale ? `/${shopLocale}` : '';
  try {
    return await parseJson(
      fetch(`${shopLocale}/products/${handle}.js`, getApiRequestConfig()),
    );
  } catch (error) {
    logError(`Product ${handle} is unavailable ... trying fallback`);
    const hostname = process.env.REACT_APP_API_HOSTNAME;
    const url = `${hostname}api/product-fallback?handle=${handle}&shop=${window.Shopify.shop}`;
    let response = await fetch(url, getApiRequestConfig());

    if (response.status === 403) {
      response = await fetch(url, getApiRequestConfig());
    }
    return await parseJson(response);
  }
}

export function getCart() {
  return parseJson(
    fetch(`/cart.js?_=${new Date().getTime()}`, {
      credentials: 'same-origin',
      cache: 'no-store',
    }),
  );
}

export async function getOffers(myshopifyDomain) {
  const hostname = process.env.REACT_APP_API_HOSTNAME;
  const previewHash = getPreviewHash();
  let url = `${hostname}api/offers?shop=${myshopifyDomain}`;
  if (previewHash) {
    // send preview hash also via get param so that cloud flare cache distinguish between preview and standard mode
    url += `&giftbox-preview=${previewHash}`;
  }
  let response = await fetch(url, getApiRequestConfig());

  if (response.status === 403) {
    return {
      offers: [],
      settings: {},
      error: API_ERRORS.PREVIEW_FORBIDDEN,
    };
  }

  response = await parseJson(response);
  if (response.offers) {
    response.offers.forEach((offer) => {
      offer.original_price = Number(offer.original_price);

      if (offer.threshold) {
        offer.threshold =
          Number(offer.threshold) * Number(window.Shopify.currency.rate);
      }
    });
    response.offers = response.offers.filter(
      (offer) =>
        (!offer.end_datetime || new Date(offer.end_datetime) > new Date()) &&
        (previewHash ? true : new Date(offer.start_datetime) <= new Date()),
    );
  }

  return response;
}

function getDiscountCode() {
  let code = localStorage.getItem('giftbox-discount');
  if (!code) {
    code = getCookie('discount_code');
  }
  return code;
}

export function createDraftOrder(
  myshopifyDomain,
  cart,
  offers,
  shippingAddress,
) {
  const hostname = process.env.REACT_APP_API_HOSTNAME;
  const data = {
    shop: myshopifyDomain,
    cart,
    offers,
    currency: window.Shopify.currency,
    shippingAddress,
    discount_code: getDiscountCode(),
  };
  return parseJson(
    fetch(`${hostname}api/create-draft-order`, {
      ...getApiRequestConfig('POST'),
      body: JSON.stringify(data),
    }),
  );
}

export function validateDiscountCode(discountCode) {
  const hostname = process.env.REACT_APP_API_HOSTNAME;
  // need to be encoded in case "+" char is included in the discount code
  const encodedDiscountCode = encodeURIComponent(discountCode);
  return parseJson(
    fetch(
      `${hostname}api/validate-discount?shop=${window.Shopify.shop}&discount_code=${encodedDiscountCode}`,
      getApiRequestConfig(),
    ),
  );
}

function sendAddedOffersToBackend(addedOffers, cartToken) {
  const addedOffersWithoutFreeShippingOffers = addedOffers.filter(
    (offer) => offer.productVariantId,
  );
  if (addedOffersWithoutFreeShippingOffers.length === 0) {
    return;
  }

  const data = {
    shop: window.Shopify.shop,
    cartToken,
    currency: window.Shopify.currency,
    addedOffers: addedOffersWithoutFreeShippingOffers,
  };

  const hostname = process.env.REACT_APP_API_HOSTNAME;
  return fetch(`${hostname}api/added-offers`, {
    ...getApiRequestConfig('POST'),
    body: JSON.stringify(data),
  });
}

export const debouncedSendAddedOffersToBackend = debounce(
  sendAddedOffersToBackend,
  1000,
);

export async function getCollectionsInCart(collectionIds, productIds) {
  if (collectionIds.length === 0 || productIds.length === 0) {
    return [];
  }

  const hostname = process.env.REACT_APP_API_HOSTNAME;
  const obj = {
    shop: window.Shopify.shop,
    collection_ids: collectionIds,
    product_ids: productIds,
  };

  const query = stringifyUrlParams(obj);

  try {
    return await parseJson(
      fetch(
        `${hostname}api/collections-in-cart?${query}`,
        getApiRequestConfig(),
      ),
    );
  } catch (error) {
    logError(error);
    return [];
  }
}

export async function getDiscountSpendings(discountCode, cart) {
  const hostname = process.env.REACT_APP_API_HOSTNAME;
  const data = {
    shop: window.Shopify.shop,
    cart: cart,
    discount_code: discountCode,
  };

  if (discountCode && cart.item_count > 0) {
    return parseJson(
      fetch(`${hostname}api/discount-spendings`, {
        ...getApiRequestConfig('POST'),
        body: JSON.stringify(data),
      }),
    );
  }
  return cart;
}
